import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseResponse, refreshCsrfToken } from "lib/fetch";
import type { AuthData, ResetPasswordInterface } from "types/authentication";
import {
    checkResetTokenRequest,
    forgotPasswordRequest,
    freshRequest,
    loginRequest,
    logoutRequest,
    resendEmailValidationRequest,
    resetPasswordRequest,
    selectRoleRequest,
    validateEmailRequest,
} from "lib/api/authentication";

export const fetchCsrfToken = () => refreshCsrfToken();

export const login = (username: string, password: string) =>
    parseResponse<AuthData>(loginRequest(username, password));

export const logout = () => parseResponse(logoutRequest());

export const selectRole = (roleId: string) =>
    parseResponse<AuthData>(selectRoleRequest(roleId));

export const fetchFresh = () => parseResponse<AuthData>(freshRequest());

export const forgotPassword = (username: string) =>
    parseResponse(forgotPasswordRequest(username));

export const checkResetToken = (token: string) =>
    parseResponse<{ username: string; token: string }>(checkResetTokenRequest(token));

export const resetPassword = (values: ResetPasswordInterface) =>
    parseResponse(resetPasswordRequest(values));

export const validateEmail = (token: string) =>
    parseResponse<{ username: string; token: string }>(validateEmailRequest(token));

export const resendEmailValidation = (username: string) =>
    parseResponse<{ username: string; token: string }>(resendEmailValidationRequest(username));

export const authenticationKeys = {
    all: ["authentication"] as const,
    fresh: () => [...authenticationKeys.all, "fresh"] as const,
};

export const useFresh = (enabled: boolean = true) =>
    useQuery({
        queryKey: authenticationKeys.fresh(),
        queryFn: fetchFresh,
        enabled,
    });

export const useLogin = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ username, password }: { username: string; password: string }) =>
            login(username, password),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authenticationKeys.all });
        },
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: logout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authenticationKeys.all });
        },
    });
};

export const useSelectRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (roleId: string) => selectRole(roleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authenticationKeys.all });
        },
    });
};

export const useForgotPassword = () =>
    useMutation({
        mutationFn: (username: string) => forgotPassword(username),
    });

export const useCheckResetToken = () =>
    useMutation({
        mutationFn: (token: string) => checkResetToken(token),
    });

export const useResetPassword = () =>
    useMutation({
        mutationFn: (values: ResetPasswordInterface) => resetPassword(values),
    });

export const useValidateEmail = () =>
    useMutation({
        mutationFn: (token: string) => validateEmail(token),
    });

export const useResendEmailValidation = () =>
    useMutation({
        mutationFn: (username: string) => resendEmailValidation(username),
    });
